// End of game: leaving a game (Back to menu, a new queue, closing or reloading the tab), concede, rematch, the
// opponent leaving a finished frame, and the result dialog's texts (components/Hud/EndOfFrame.tsx).
//
// Left games: a finished online game this page has closed is remembered (Match.leftGameIds), and anything that still
// arrives for it (game:state, game:notice) is ignored, so its end-of-frame dialog never comes back.
//
// Across a reload: only the left games are kept, in sessionStorage (LEFT_GAMES_KEY, the last MAX_LEFT_GAMES ids, this
// tab only). Closing or reloading the tab while a finished frame's result is shown leaves it like Back to menu
// (pagehide), so it stays closed on the reloaded page. Nothing else survives a reload: a game still being played is
// picked up again from the server (game:sync on connect), and a shot that was rolling is lost (activeShotId lives in
// memory only), so the server abandons it.
import {GameStore} from "../features/game/game-store";
import {Net} from "./network";
import type {HudPlayer, HudState} from "../features/game/game-store";
import type {MatchCore, MatchMode, OnlineInfo} from "./types";
import type {GameNoticeMsg} from "./messages";

export const LEFT_GAMES_KEY = "snooker.leftGames";
export const MAX_LEFT_GAMES = 20;

/** sessionStorage, or null when the browser won't give it (privacy mode, sandboxed frame). */
type StorageLike = Pick<Storage, "getItem" | "setItem">;
const sessionStore = (): StorageLike => sessionStorage;

/** The stored list: string ids only; anything unreadable is an empty list. */
export function parseLeftGameIds(raw: string | null): Set<string> {
    const ids = JSON.parse(raw || "[]");
    return new Set(Array.isArray(ids) ? ids.filter(x => typeof x === "string") : []);
}

export function loadLeftGameIds(storage: () => StorageLike = sessionStore): Set<string> {
    try { return parseLeftGameIds(storage().getItem(LEFT_GAMES_KEY)); }
    catch { return new Set(); }
}

/** What is written back: the most recent MAX_LEFT_GAMES ids, oldest first. */
export function serializeLeftGameIds(ids: Set<string>): string {
    return JSON.stringify(Array.from(ids).slice(-MAX_LEFT_GAMES));
}

export function rememberLeftGame(ids: Set<string>, gameId: string, storage: () => StorageLike = sessionStore) {
    ids.add(gameId);
    try { storage().setItem(LEFT_GAMES_KEY, serializeLeftGameIds(ids)); } catch {}
}

interface Where { mode: MatchMode; online: OnlineInfo | null }

/** A finished online frame is on screen (its result dialog). */
export function finishedOnlineGame(m: Where): boolean {
    return m.mode === "online" && m.online?.status === "over";
}

/** Concede: online while playing tells the server; practice restarts the frame; nothing otherwise. */
export function concedeAction(m: Where): "send" | "restart-practice" | "none" {
    if (m.mode === "online" && m.online && m.online.status === "playing") return "send";
    if (m.mode === "practice") return "restart-practice";
    return "none";
}

/** Rematch: online asks the server (the server decides); practice just starts a new frame. */
export function rematchAction(m: Where): "send" | "restart-practice" {
    return m.mode === "online" && m.online ? "send" : "restart-practice";
}

/** The opponent closed the finished frame: they are gone and no rematch is possible any more. */
export function withOpponentGone(online: OnlineInfo): OnlineInfo {
    const you = online.you;
    return {
        ...online,
        players: online.players.map((p, i) => i === you ? p : {...p, connected: false}),
        rematch: [false, false],
    };
}

// ----- result dialog texts (EndOfFrame.tsx) -----

export const END_REASONS: Record<string, string> = {
    black: "Final black potted",
    cleared: "Table cleared: the higher score wins",
    "black-foul": "Foul on the final black",
    "black-reload": "Foul on the final black: the shot was abandoned (reload or lost connection)",
    abandoned: "Both players left: the frame was abandoned",
    concede: "Frame conceded",
    disconnect: "Opponent did not reconnect",
    time: "Opponent ran out of time",
    "opponent left": "Opponent left the table",
};

/** Why the online frame ended, from our side. */
export function endReasonText(endReason: string | null, won: boolean, opponentName: string | undefined): string | null {
    if (endReason === "time") return won ? END_REASONS.time : "You ran out of time";
    return endReason === "disconnect" || endReason === "opponent left"
        ? (won ? END_REASONS[endReason] : endReason === "disconnect" ? "You were disconnected too long" : "You left the table")
        : endReason === "concede" ? (won ? `${opponentName} conceded` : "You conceded") : (END_REASONS[endReason || ""] || endReason);
}

export interface RematchView {
    opponent: HudPlayer | undefined;
    iAsked: boolean;
    theyAsked: boolean;
    /** "X wants a rematch!" */
    showTheyAsked: boolean;
    buttonDisabled: boolean;
    buttonText: string;
}

export function rematchView(hud: Pick<HudState, "players" | "you" | "rematch">): RematchView {
    const {players, you, rematch} = hud;
    const opponent = players[1 - you];
    const iAsked = rematch[you], theyAsked = rematch[1 - you];
    return {
        opponent, iAsked, theyAsked,
        showTheyAsked: theyAsked && !iAsked,
        buttonDisabled: iAsked || !opponent?.connected,
        buttonText: iAsked ? `Waiting for ${opponent?.name}…` : "Rematch",
    };
}

// ----- called through Match -----

/** Match.init: closing or reloading the tab while the result of a finished frame is shown leaves it, like "Back to menu". */
export function listenForPagehide(m: MatchCore): () => void {
    const onPagehide = () => { if (finishedOnlineGame(m)) m.leaveOnlineGame(); };
    window.addEventListener("pagehide", onPagehide);
    return () => window.removeEventListener("pagehide", onPagehide);
}

/** The one way out of an online game, whatever the exit (Back to menu, header practice, a new queue, closing the tab). */
export function leaveOnlineGame(m: MatchCore) {
    if (m.mode !== "online" || !m.online) return;
    const {gameId, status} = m.online;
    if (status === "over") rememberLeftGame(m.leftGameIds, gameId);
    Net.leave({gameId});
}

/** Before queueing for a new match: a finished game still on screen is left first. */
export function leaveFinishedGame(m: MatchCore) {
    if (finishedOnlineGame(m)) m.startPractice();
}

export function concede(m: MatchCore) {
    const action = concedeAction(m);
    if (action === "send") Net.concede({gameId: m.online!.gameId});
    else if (action === "restart-practice") m.startPractice();
}

export function rematch(m: MatchCore) {
    if (rematchAction(m) === "send") Net.rematch({gameId: m.online!.gameId});
    else m.startPractice();
}

export function backToMenu(m: MatchCore) {
    m.startPractice();   // leaves the online game first (leaveOnlineGame)
}

/** socket "game:notice": a message without state, e.g. "X left the table." after the frame ended. */
export function onNotice(m: MatchCore, msg: GameNoticeMsg | null | undefined) {
    if (!msg || typeof msg.notice !== "string" || m.leftGameIds.has(msg.gameId)) return;
    if (m.mode !== "online" || !m.online || m.online.gameId !== msg.gameId) return;
    GameStore.toast(msg.notice, "warn", 6000);
    if (msg.opponentLeft && m.online.status === "over") {
        m.online = withOpponentGone(m.online);
        m.publish();
    }
}

/** The frame is over: free the mouse for the result dialog. */
export function releasePointerForResult() {
    try { document.exitPointerLock && document.exitPointerLock(); } catch {}
}
